import { LandingPageContent } from '../types';

/**
 * Occasion page for christening/baptism gifting. Written with the Greek
 * Orthodox vaptisi tradition in view (godparents carry real gifting duties
 * there), but serving the general "christening gifts" query too — including
 * the large Greek diaspora searching in English.
 */
const christeningGifts: LandingPageContent = {
	path: '/gifts/christening-gifts',
	seoTitle: 'Christening & Baptism Gifts — Personalized Keepsakes for a Godchild',
	metaDescription:
		'Christening and baptism gift ideas that outlast the day: personalized storybooks starring the child, keepsakes with their name, and gifts a godparent can be proud of.',
	heading: 'Christening & Baptism Gifts',
	subheading: 'A keepsake for the one day that names them.',
	heroImage: '/assets/hero/kid-3/watercolor_scene.png',
	breadcrumbParents: [{ name: 'Gifts for kids', url: '/gifts-for-kids' }],
	intro: `A christening is the rare children's occasion where the gift is expected to *last*. Nobody brings a plastic toy to a baptism. The brief — spoken or not — is a keepsake: something with the child's name on it, something that will still be around when they are old enough to ask about the day.

The classics are silver spoons, icons, jewellery and photo frames. All fine, and all destined for a drawer. The alternative we'd argue for is a keepsake the child will actually use: a book in which they are the illustrated hero, with their name — the very name given on the day — woven through the story.

For godparents especially, in traditions where the *nonos* or *nona* carries real gifting duties, it is a present that shows thought rather than expense.`,
	sections: [
		{
			heading: 'What makes a good christening gift',
			body: `Three qualities, in order:

1. **The name.** A christening is, at its heart, a naming. The strongest gifts carry the child's name — not engraved on an object they'll never handle, but somewhere they will encounter it. A story where the name appears on every other page does that literally.
2. **Permanence.** The gift should survive to be shown to the child later. Printed and bound beats digital; a book beats a toy.
3. **A connection to the giver.** The best christening gifts are ones where, years later, the child knows who gave it. A book gifted by a godparent — with room for a written dedication — builds that link in.`,
		},
		{
			heading: 'For godparents: the vaptisi problem',
			body: `In Greek Orthodox tradition the godparent doesn't just attend the baptism — they are half the event. The candles, the oil, the *ladopana*, often the outfit: the nonos or nona provides them, and then, on top of everything, there is the question of the gift.

The conventional answer is gold — a cross, a bracelet. Beautiful, expensive, and worn once a year.

A personalized book is a different kind of answer. It costs less, says more, and gets used weekly rather than annually. And because the godparent chose the story and wrote the dedication, it remains *their* gift in a way an off-the-shelf item never is. Add the gold cross if tradition demands; add the book because the child will love it.

Stories can be produced in Greek as well as English — see [personalized Greek books](/personalized-greek-books-for-kids) — so the gift can match the language of the family, or of the yiayia who will do most of the reading.`,
		},
		{
			heading: 'Timing: babies grow into books',
			body: `Most children are baptised before they can read — often before they can sit up. That is not a problem; it is the point of a keepsake.

A personalized book given at a christening has two lives. First as an object on the nursery shelf, read aloud by parents who enjoy the joke that the baby is fighting dragons. Then, at three or four, the real payoff: the child recognises themselves, learns that this book arrived on the day they were named, and claims it completely.

If you would rather the likeness match a slightly older child, it is also entirely reasonable to give the book at the first birthday or name day instead — no one audits christening gifts for punctuality.`,
		},
		{
			heading: 'A note on dedications',
			body: `Whatever you give, write in it. Date, occasion, your name, one sentence.

The silver spoon says nothing about who gave it. A book with *"For your baptism — from your nona, who promised to spoil you"* inside the cover is a document. Twenty years later, that page is the part that matters most.`,
		},
	],
	picks: [
		{
			label: 'Personalized storybooks',
			href: '/personalized-story-books',
			description: 'Their name and their face in a printed story — the keepsake that gets used.',
		},
		{
			label: 'Personalized gifts for kids',
			href: '/personalized-gifts-for-kids',
			description: 'The full range: books, prints and keepsakes.',
		},
	],
	faqs: [
		{
			question: 'What is a good christening gift?',
			answer:
				'The strongest christening gifts carry the child’s name and last long enough to be shown to them later. Personalized storybooks — where the child appears as the illustrated hero and their name runs through the story — work well because they are both a keepsake and something the child will actually use.',
		},
		{
			question: 'What does a godparent traditionally give at a Greek Orthodox baptism?',
			answer:
				'The godparent traditionally provides the baptismal candle, oil, ladopana and often the outfit, plus a gift — conventionally a gold cross or jewellery. Many godparents now add a personalized keepsake such as a book starring the child, which gets daily use in a way jewellery does not.',
		},
		{
			question: 'Is a personalized book suitable for a baby?',
			answer:
				'Yes, as a keepsake that grows with them. It is read to the child at first, and claimed by the child at three or four when they recognise themselves. Books given at a christening tend to be the ones still on the shelf a decade later.',
		},
		{
			question: 'Can the book be in Greek?',
			answer:
				'Yes. Stories can be produced in Greek as well as English, so the gift can match the language of the family or of the grandparents who will read it aloud.',
		},
	],
	related: [
		{ label: 'Personalized gifts for kids', href: '/personalized-gifts-for-kids' },
		{ label: 'Personalized Greek books for kids', href: '/personalized-greek-books-for-kids' },
		{ label: 'Name day gift ideas', href: '/blog/name-day-gift-ideas' },
	],
	cta: { label: 'Browse keepsake gifts', href: '/products' },
};

export default christeningGifts;
